import { inject, PLATFORM_ID, TransferState } from '@angular/core';
import { NavigationEnd, ResolveFn, Router } from '@angular/router';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { catchError, delay, filter, Observable, of, tap } from 'rxjs';
import { PageResolverResopnse } from '../types/page-resolver-response.type';
import { PageLayoutService } from '../services/page-resolver.service';
import { MetaService } from '../services/meta.service';
import { RESPONSE } from '../../../express.token';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { LayoutService } from '../services/layout.service';
import { DEFAULT_PHONE_NUMBER_PROMPT, GlobalService, PLATFORM_SETTINGS_KEY } from '../../common/services/global.service';
import { LayoutBuilderService } from '../services/layout-builder.service';
import { PiwikService } from '../services/piwik-service';
import { environment } from '../../../environments/environment';

export function resolveIsGtm(trackingId: string, trackingMethod?: string): boolean {
    if (trackingMethod) {
        return trackingMethod === 'gtm';
    }
    return trackingId.startsWith('GTM-');
}

function getFullPath(route: ActivatedRouteSnapshot): string {
    if (route.children.length === 0) {
        return route.url.join('/');
    }

    const childrenPaths = route.children.map((child) => getFullPath(child));
    return route.url.join('/') + '/' + childrenPaths.join('/');
}

export const websiteLayoutResolver = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
    const layoutService = inject(LayoutService);
    const platformId = inject(PLATFORM_ID);
    const layoutBuilderService = inject(LayoutBuilderService);
    const router = inject(Router);
    const res = inject(RESPONSE, { optional: true });
    const globalService = inject(GlobalService);
    const transferState = inject(TransferState);
    const piwikService = inject(PiwikService);
    const url = getFullPath(route).replace(/^\/+|\/+$/g, '');

    /**
     * For loading google analytics
     * @returns
     */
    const loadGoogleAnalytics = (trackingId: string, trackingMethod?: string) => {
        if (isPlatformServer(platformId)) {
            return;
        }

        const isGtm = resolveIsGtm(trackingId, trackingMethod);

        /**
         * Injects the Google Tag Manager script and noscript tags into the document.
         * Skips injection if the script is already present to avoid duplicates.
         */
        const injectGTM = () => {
            // Guard against malformed/untrusted IDs before interpolating into inline script.
            if (!/^GTM-[A-Z0-9]+$/.test(trackingId)) {
                return;
            }

            if (document.getElementById('google-tag-manager-script')) {
                return;
            }

            const gtmScript = document.createElement('script');
            gtmScript.id = 'google-tag-manager-script';
            gtmScript.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${trackingId}');`;

            document.head.appendChild(gtmScript);
        };

        /**
         * Injects the GA4 measurement script into the document head.
         * Skips injection if the script is already present to avoid duplicates.
         */
        const injectGA4 = () => {
            if (document.getElementById('google-analytics-script')) {
                return;
            }

            const gaScript1 = document.createElement('script');
            gaScript1.id = 'google-analytics-script';
            gaScript1.setAttribute('defer', 'true');
            gaScript1.setAttribute(
                'src',
                `https://www.googletagmanager.com/gtag/js?id=${trackingId}`,
            );

            const gaScript2 = document.createElement('script');
            gaScript2.textContent = `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag(\'js\', new Date());gtag(\'config\', \'${trackingId}\');`;

            document.documentElement?.firstChild?.appendChild(gaScript1);
            document.documentElement?.firstChild?.appendChild(gaScript2);
        };

        /**
         * Defers script injection until the browser is idle using requestIdleCallback,
         * falling back to setTimeout for unsupported browsers.
         */
        const injectWhenIdle = () => {
            const injectTrackingScript = isGtm ? injectGTM : injectGA4;

            if ('requestIdleCallback' in window) {
                (window as any).requestIdleCallback(injectTrackingScript);
            } else {
                setTimeout(injectTrackingScript, 0);
            }
        };

        if (document.readyState === 'complete') {
            injectWhenIdle();
        } else {
            window.addEventListener('load', injectWhenIdle, { once: true });
        }
    };

    // Not returning this observable to not block the pages api request
    // instead returning a null observable
    layoutService.getWebsiteLayout(url).subscribe({
        next: (data) => {
            const layoutSettings = data.body?.data;           

            // TODO: need to remove layoutSettings?.settings?.layout later
            layoutBuilderService.layoutSections =
                layoutSettings?.settings?.layout || layoutSettings?.website_layout?.layout || [];

            //save branding to global service
            if (layoutSettings.branding) {
                const b = layoutSettings.branding;
                const enrichedBranding = {
                    ...b,
                    fallback_image: b.fallback_image || b.derived_fallback_image || '',
                    og_fallback_image: b.og_fallback_image || b.derived_og_fallback_image || '',
                    inverted_company_logo: b.inverted_company_logo || b.derived_inverted_company_logo || '',
                };
                globalService.branding.next(enrichedBranding);

                if (isPlatformServer(platformId) && res) {
                    if (enrichedBranding.companyFavicon || enrichedBranding.company_favicon) {
                        res.locals = {
                            ...res.locals,
                            favicon: enrichedBranding.companyFavicon || enrichedBranding.company_favicon,
                        };
                    }
                    if (enrichedBranding.fallback_image) {
                        res.locals = {
                            ...res.locals,
                            fallback_image: enrichedBranding.fallback_image,
                        };
                    }
                }
            }

            //save communication to global service
            if (layoutSettings.communication) {
                globalService.communication.next(layoutSettings.communication);
            }

            //save listing page to global service
            if (layoutSettings.listing_page) {
                globalService.listingPageSettings.next(layoutSettings.listing_page);
            }

            //save search filter to global service
            if (layoutSettings?.search_settings?.['filter-setting']) {
                const formattedSearchFilter: { [key: string]: { display: boolean; options: string[] }} = {};

                Object.keys(layoutSettings.search_settings['filter-setting']).forEach((key) => {
                    if(!['id', 'uuid', 'created_at', 'updated_at', 'settings_name'].includes(key)) {
                        formattedSearchFilter[key] = layoutSettings.search_settings['filter-setting'][key];
                    }
                });
                globalService.searchFilterSettings.next(formattedSearchFilter);
            }

            //save theme settings to global service
            if (layoutSettings.theme) {
                globalService.theme.next(layoutSettings.theme);
            }

            //save blog global settings to global service
            if (layoutSettings.blog) {
                globalService.blogGlobalSettings.next(layoutSettings.blog);
            }

            // save platform settings (e.g. company timezone) to global service
            if (layoutSettings.platform) {
                globalService.platformSettings.next(layoutSettings.platform);
                // Transfer to the client so the date pipe has the timezone synchronously at hydration
                if (isPlatformServer(platformId)) {
                    transferState.set(PLATFORM_SETTINGS_KEY, layoutSettings.platform);
                }
            }

            globalService.listingsCount.next(layoutSettings?.listings_count || 0);

            // Update menu to layout builder service
            if (layoutSettings?.menu) {
                layoutBuilderService.siteMenus = layoutSettings.menu;
            }
            if (layoutSettings?.default_state) {
                globalService.defaultState.next(layoutSettings?.default_state || '');
            }
            if (layoutSettings?.state_codes) {
                globalService.stateCodes.next(layoutSettings?.state_codes || []);
            }
            
            globalService.googleSSOSettings.next({
                client_id: layoutSettings?.google_sso?.client_id || environment.g_client_id,
                google_one_tap_status: layoutSettings?.google_sso?.google_one_tap_status ?? true,
                blog_one_tap_status: layoutSettings?.google_sso?.blog_one_tap_status ?? false,
                status: layoutSettings?.google_sso?.status ?? true,
            });

            if(layoutSettings?.editor) {
                globalService.editorSettings.next(layoutSettings?.editor);
            }

            if (layoutSettings?.gtm_id?.tracking_id) {
                globalService.gtmId.next(layoutSettings?.gtm_id.tracking_id);
                loadGoogleAnalytics(
                    layoutSettings.gtm_id.tracking_id,
                    layoutSettings.gtm_id.tracking_method,
                );
            }

            if (layoutSettings?.piwik?.site_id && layoutSettings?.piwik?.account_name) {
                globalService.piwikConfig.next({
                    site_id: layoutSettings?.piwik?.site_id,
                    account_name: layoutSettings?.piwik?.account_name,
                });
                // piwikService.initialize();
            }

            // Defaulted here rather than on the subject itself: this resolver does not block
            // navigation, so a pre-set default can open the prompt before a site's saved "off"
            // arrives. Set above the redirect return so every site gets it (AGE-RKDRGE).
            if (layoutSettings) {
                globalService.phoneNumberPrompt.next(
                    layoutSettings.registration?.phone_number_prompt ?? DEFAULT_PHONE_NUMBER_PROMPT,
                );
            }

            if (layoutSettings?.redirection) {
                if (isPlatformServer(platformId)) {
                    res?.redirect(
                        layoutSettings.redirection.redirection_type,
                        `/${layoutSettings?.redirection?.redirect_url}`,
                    );
                } else {
                    router.navigateByUrl(`/${layoutSettings?.redirection?.redirect_url}`);
                }
                return;
            }

            if (layoutSettings?.registration){
                const forcedRegSettings = layoutSettings.registration?.forced_registration;
                globalService.forcedRegistration.next(forcedRegSettings);

                if (layoutSettings.registration?.forced_registration_modal){
                    globalService.forced_registration_modal.next(layoutSettings.registration?.forced_registration_modal);
                }
            }

            
            if (layoutSettings?.custom_site_snippets) {
                globalService.customSiteSnippets.next(layoutSettings.custom_site_snippets);
            }

            globalService.closedPropertiesEnabled.next(layoutSettings?.closed_properties_enabled ?? false);

            if (layoutSettings?.company) {
                globalService.companyDetails.next(layoutSettings.company);
            }


            // if (layoutSettings?.legacy_redirection && layoutSettings?.legacy_redirection?.legacy_path &&
            //     !['blog', 'east-nashville', 'west-nashville', 'belle-meade', 'hendersonville', 'sitemap'].includes(layoutSettings?.legacy_redirection?.legacy_path)
            // ) {
            //     if (isPlatformServer(platformId)) {
            //         res?.redirect(
            //             301,
            //             `/${layoutSettings?.legacy_redirection?.redirect_url || ''}`,
            //         );
            //     } else {
            //         router.navigateByUrl(`/${layoutSettings?.legacy_redirection?.redirect_url || ''}`);
            //     }
            // }
        },
        error: (error) => {},
    });

    return of(null);
};
